
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

export default function Services() {
  useEffect(() => {
    // Update page title and meta
    document.title = "川崎市訪問介護サービス内容 - 身体介護・生活援助 | ゆきのケア";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '川崎市の訪問介護ゆきのケアのサービス内容。身体介護（入浴・排泄・食事介助）と生活援助（掃除・洗濯・調理）を提供。川崎市幸区・川崎区・横浜市鶴見区対応。');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', '訪問介護,サービス内容,身体介護,生活援助,川崎市,入浴介助,食事介助');
    }

    // Schema.org JSON-LD for Service
    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "訪問介護サービス",
      "description": "川崎市の訪問介護ゆきのケアが提供する身体介護・生活援助サービス",
      "provider": {
        "@type": "LocalBusiness",
        "name": "訪問介護ゆきのケア",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "戸手本町1-123-12 ドルフ5 203",
          "addressLocality": "川崎市幸区",
          "addressRegion": "神奈川県",
          "postalCode": "212-0023",
          "addressCountry": "JP"
        }
      },
      "serviceType": ["身体介護", "生活援助"],
      "areaServed": ["川崎市幸区", "川崎市川崎区", "横浜市鶴見区"],
      "hoursAvailable": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        "opens": "08:00",
        "closes": "18:00"
      }
    };

    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "川崎市訪問介護サービス内容 - 身体介護・生活援助",
      "description": "川崎市の訪問介護ゆきのケアのサービス内容。身体介護（入浴・排泄・食事介助）と生活援助（掃除・洗濯・調理）を提供。",
      "url": `${import.meta.env.VITE_SITE_URL}/services`,
      "mainEntity": serviceSchema
    };

    // Add schema to head
    const script1 = document.createElement('script');
    script1.type = 'application/ld+json';
    script1.textContent = JSON.stringify(serviceSchema);
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.type = 'application/ld+json';
    script2.textContent = JSON.stringify(webPageSchema);
    document.head.appendChild(script2);

    return () => {
      // Cleanup
      document.head.removeChild(script1);
      document.head.removeChild(script2);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-green-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <img 
                src="https://static.readdy.ai/image/4e28e17ba4e737ad2b4d0213d4252ea8/96aaf8d56f57e04898441455e82ac9b1.png" 
                alt="訪問介護ゆきのケア" 
                title="訪問介護ゆきのケア 川崎市"
                className="h-16 w-auto object-contain"
              />
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-700 hover:text-blue-600 font-medium">ホーム</Link>
              <Link to="/services" className="text-blue-600 font-medium">サービス内容</Link>
              <Link to="/careers" className="text-gray-700 hover:text-blue-600 font-medium">採用情報</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="relative h-96 flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url('https://public.readdy.ai/ai/img_res/839d574cb4ac1612c2b93a19c976d4a1.jpg')`
        }}
      >
        <div className="text-center text-white px-4 max-w-4xl">
          <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{fontFamily: 'Noto Sans JP, sans-serif'}}>
            安心できる生活のそばに。
          </h2>
          <p className="text-xl md:text-2xl font-medium">
            まごころを込めて、あなたの暮らしを支えます。
          </p>
        </div>
      </section>

      {/* サービス概要 */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-16" style={{fontFamily: 'Noto Sans JP, sans-serif'}}>
            訪問介護とは？
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="bg-blue-50 rounded-xl p-8 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                訪問介護（ホームヘルプサービス）は、介護が必要な方のご自宅に訪問し、日常生活の支援や身体介助を行うサービスです。
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                ゆきのケアでは、ご利用者さまが住み慣れたご自宅で安心して過ごせるよう、やさしさと誠実さを大切にしています。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 提供サービス詳細 */}
      <section className="py-20 bg-gradient-to-r from-blue-50 to-green-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-16" style={{fontFamily: 'Noto Sans JP, sans-serif'}}>
            サービス内容
          </h2>
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-heart-pulse-line text-4xl text-blue-600"></i>
                </div>
                <h3 className="text-3xl font-bold text-gray-800 mb-4">身体介護</h3>
                <img 
                  src="https://readdy.ai/api/search-image?query=Japanese%20caregiver%20in%20comfortable%20casual%20work%20clothes%20gently%20helping%20elderly%20person%20with%20personal%20care%20in%20clean%20Japanese%20home%20bathroom%2C%20respectful%20assistance%20with%20daily%20hygiene%20activities%2C%20modern%20Japanese%20bathroom%20with%20natural%20lighting%2C%20caring%20atmosphere%20showing%20dignity%20and%20respect%2C%20realistic%20photography%20with%20natural%20expressions%20and%20genuine%20human%20interaction&width=600&height=300&seq=physical4&orientation=landscape" 
                  alt="身体介護" 
                  className="w-full h-48 object-cover rounded-lg mb-6"
                />
              </div>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-blue-600 text-xl"></i>
                  <span className="text-gray-700">入浴介助</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-blue-600 text-xl"></i>
                  <span className="text-gray-700">排泄介助</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-blue-600 text-xl"></i>
                  <span className="text-gray-700">食事介助</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-blue-600 text-xl"></i>
                  <span className="text-gray-700">着替え介助</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-blue-600 text-xl"></i>
                  <span className="text-gray-700">体位変換</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-blue-600 text-xl"></i>
                  <span className="text-gray-700">移動介助</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-home-heart-line text-4xl text-green-600"></i>
                </div>
                <h3 className="text-3xl font-bold text-gray-800 mb-4">生活援助</h3>
                <img 
                  src="https://readdy.ai/api/search-image?query=Japanese%20home%20care%20worker%20in%20casual%20comfortable%20clothing%20helping%20with%20household%20tasks%20in%20traditional%20Japanese%20home%2C%20caregiver%20wearing%20everyday%20work%20clothes%20assisting%20with%20cooking%20and%20cleaning%20in%20Japanese%20kitchen%2C%20organized%20Japanese%20living%20space%20with%20natural%20elements%2C%20warm%20domestic%20environment%20with%20soft%20natural%20lighting%2C%20realistic%20photography%20showing%20genuine%20daily%20life%20support&width=600&height=300&seq=life4&orientation=landscape" 
                  alt="生活援助" 
                  className="w-full h-48 object-cover rounded-lg mb-6"
                />
              </div>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-green-600 text-xl"></i>
                  <span className="text-gray-700">掃除・整理整頓</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-green-600 text-xl"></i>
                  <span className="text-gray-700">洗濯・衣類管理</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-green-600 text-xl"></i>
                  <span className="text-gray-700">調理・配膳</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-green-600 text-xl"></i>
                  <span className="text-gray-700">買い物代行</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-green-600 text-xl"></i>
                  <span className="text-gray-700">見守り支援</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-green-600 text-xl"></i>
                  <span className="text-gray-700">服薬管理</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 対応エリア・時間 */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-16" style={{fontFamily: 'Noto Sans JP, sans-serif'}}>
            サービス提供エリア・時間
          </h2>
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-blue-50 rounded-xl p-8 text-center">
              <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-map-pin-line text-5xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6">対応地域</h3>
              <div className="space-y-3 text-lg">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <span className="font-semibold text-blue-600">川崎市幸区</span>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <span className="font-semibold text-blue-600">川崎市川崎区</span>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <span className="font-semibold text-blue-600">横浜市鶴見区</span>
                </div>
              </div>
            </div>
            <div className="bg-green-50 rounded-xl p-8 text-center">
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-5xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6">サービス提供時間</h3>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <p className="text-3xl font-bold text-green-600 mb-2">8:00～18:00</p>
                <p className="text-gray-600">月曜日～日曜日</p>
                <p className="text-sm text-gray-500 mt-4">
                  ※時間外のサービスについては<br />お気軽にご相談ください
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ご利用の流れ */}
      <section className="py-20 bg-gradient-to-r from-orange-50 to-pink-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-800 mb-16" style={{fontFamily: 'Noto Sans JP, sans-serif'}}>
            ご利用の流れ
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-orange-600">1</span>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">お問い合わせ・ご相談</h3>
              <p className="text-gray-600 text-sm">
                まずはお電話またはお問い合わせフォームよりご連絡ください
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">2</span>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">ケアプラン作成</h3>
              <p className="text-gray-600 text-sm">
                ケアマネジャーがお客様に最適なケアプランを作成いたします
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">3</span>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">ご契約・訪問開始</h3>
              <p className="text-gray-600 text-sm">
                サービス内容にご納得いただけましたらご契約、サービス開始となります
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-pink-600">4</span>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">定期的なモニタリング</h3>
              <p className="text-gray-600 text-sm">
                サービス内容を定期的に見直し、より良いケアを提供いたします
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">株式会社歩</h3>
              <div className="space-y-2 text-gray-300">
                <p>〒212-0023 神奈川県川崎市幸区戸手本町1-123-12 ドルフ5 203</p>
                <p>Tel：044-280-7266</p>
                <p>Fax：044-280-7267</p>
                <p>介護保険事業所番号：1475102685</p>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">会社情報</h3>
              <div className="space-y-2 text-gray-300">
                <p>設立：平成29年2月1日</p>
                <p>資本金：200万円</p>
                <p>代表取締役：吉田 香</p>
                <p>管理者：菊池 雪野</p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center">
            <p className="text-gray-400">© 2025 訪問介護ゆきのケア All Rights Reserved.</p>
            <a href="https://readdy.ai/?origin=logo" className="text-blue-400 hover:text-blue-300 ml-4">Website Builder</a>
          </div>
        </div>
      </footer>

      {/* 固定お問い合わせボタン */}
      <div className="fixed bottom-6 right-6 z-50">
        <a 
          href="https://ws.formzu.net/dist/S634209674/" 
          target="_blank" 
          rel="noopener noreferrer"
          className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full shadow-lg font-semibold transition-colors whitespace-nowrap cursor-pointer inline-flex items-center"
        >
          <i className="ri-phone-line mr-2"></i>
          お問い合わせはこちら
        </a>
      </div>
    </div>
  );
}
